/**
 * Currency utility functions
 */

/**
 * Currency symbol mapping
 */
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  CAD: 'C$',
  AUD: 'A$',
  JPY: '¥',
  CNY: '¥',
  INR: '₹',
  BRL: 'R$',
  MXN: '$',
  CHF: 'CHF',
  SEK: 'kr',
  NOK: 'kr',
  DKK: 'kr',
  PLN: 'zł',
  CZK: 'Kč',
  HUF: 'Ft',
  RON: 'lei',
  BGN: 'лв',
  HRK: 'kn',
  RUB: '₽',
  TRY: '₺',
  THB: '฿',
  IDR: 'Rp',
  MYR: 'RM',
  PHP: '₱',
  SGD: 'S$',
  VND: '₫',
  ZAR: 'R',
  HKD: 'HK$',
  NZD: 'NZ$',
  KRW: '₩',
  AED: 'د.إ',
  SAR: 'ر.س',
  EGP: 'E£',
  NGN: '₦',
  KES: 'KSh',
};

/**
 * Get currency symbol for a given currency code
 * @param currency - ISO currency code (e.g., 'USD', 'EUR')
 * @returns Currency symbol (e.g., '$', '€')
 */
export function getCurrencySymbol(currency: string): string {
  return CURRENCY_SYMBOLS[currency.toUpperCase()] || currency;
}

/**
 * Format amount with currency symbol
 * @param amount - Amount to format
 * @param currency - ISO currency code
 * @returns Formatted string (e.g., '$10.00', '€10.00')
 */
export function formatCurrency(amount: number, currency: string): string {
  const symbol = getCurrencySymbol(currency);
  const formatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${symbol}${formatter.format(amount)}`;
}

/**
 * Format price without trailing zeros
 * @param price - Price to format
 * @returns Formatted string (e.g., '10', '10.5', '10.99')
 */
export function formatPrice(price: number): string {
  // Remove trailing zeros: 10.00 -> 10, 10.50 -> 10.5, 10.99 -> 10.99
  return price.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
}
